import { useCallback, useEffect, useState } from 'react'
import { webFiltrosApi } from '../api/webFiltros'
import { colors, text, space, radius } from '../design/tokens'

/**
 * Filtros de la tienda (Martin, 6 oct 2026): qué filtros ve el cliente en cada
 * categoría. Cada filtro muestra los valores que hoy le llegan de Presea — se
 * regeneran solos con cada sincronización; acá solo se decide si el cliente
 * ve el filtro o no.
 */
export default function FiltrosTiendaPage() {
  const [categorias, setCategorias] = useState([])
  const [actual, setActual] = useState('vinos')
  const [filtros, setFiltros] = useState([])
  const [cargando, setCargando] = useState(true)
  const [guardando, setGuardando] = useState(null)
  const [msg, setMsg] = useState('')

  const cargarCategorias = useCallback(() => {
    webFiltrosApi.categorias()
      .then(({ data }) => setCategorias(data.data))
      .catch(() => setMsg('No se pudieron cargar las categorías.'))
  }, [])
  useEffect(() => { cargarCategorias() }, [cargarCategorias])

  useEffect(() => {
    setCargando(true)
    setMsg('')
    webFiltrosApi.filtros(actual)
      .then(({ data }) => setFiltros(data.data))
      .catch(() => setMsg('No se pudieron cargar los filtros de esta categoría.'))
      .finally(() => setCargando(false))
  }, [actual])

  const cambiar = async (f) => {
    setGuardando(f.key)
    setMsg('')
    try {
      await webFiltrosApi.cambiar(actual, f.key, !f.visible)
      setFiltros((fs) => fs.map((x) => (x.key === f.key ? { ...x, visible: !f.visible } : x)))
      cargarCategorias()
    } catch {
      setMsg('No se pudo guardar el cambio.')
    } finally {
      setGuardando(null)
    }
  }

  return (
    <div style={{ maxWidth: 1100 }}>
      <h2 style={st.title}>Filtros de la tienda</h2>
      <p style={st.sub}>
        Elegí qué filtros ve el cliente en cada categoría de la tienda. Los valores de cada filtro
        salen de Presea y se actualizan solos con cada sincronización; acá solo se prende o se apaga
        el filtro entero. Los números son los mismos que ve el cliente (incluyen los productos agotados que
        la tienda muestra). El cambio se ve en la tienda en uno o dos minutos.
      </p>

      <div style={st.cats}>
        {categorias.map((c) => (
          <button key={c.slug} type="button" onClick={() => setActual(c.slug)}
            style={{ ...st.cat, ...(c.slug === actual ? st.catActiva : {}) }}>
            {c.nombre}
            {c.ocultos > 0 && <span style={st.catOcultos}>{c.ocultos} oculto{c.ocultos === 1 ? '' : 's'}</span>}
          </button>
        ))}
      </div>

      {msg && <p style={st.msg}>{msg}</p>}

      {cargando ? (
        <p style={st.vacio}>Cargando…</p>
      ) : filtros.length === 0 ? (
        <p style={st.vacio}>Esta categoría no tiene filtros con valores.</p>
      ) : (
        <div style={st.tabla}>
          <div style={{ ...st.fila, ...st.cabecera }}>
            <span>Lo ve el cliente</span>
            <span>Filtro</span>
            <span>Valores de hoy</span>
          </div>
          {filtros.map((f) => (
            <div key={f.key} style={{ ...st.fila, ...(f.visible ? {} : st.filaOculta) }}>
              <label style={st.check}>
                <input type="checkbox" checked={f.visible} disabled={guardando === f.key} onChange={() => cambiar(f)} />
                <span style={{ color: f.visible ? colors.ok : colors.textMeta }}>{f.visible ? 'Sí' : 'No'}</span>
              </label>
              <span style={st.nombre}>
                {f.label}
                <span style={st.cantidad}>{f.cantidad} valor{f.cantidad === 1 ? '' : 'es'}</span>
                {f.visible && f.un_solo_valor && <span style={st.aviso}>La tienda no lo muestra: con un solo valor no hay nada que elegir</span>}
              </span>
              <span style={st.valores}>
                {f.valores.map((v) => (
                  <span key={v.valor} style={st.valor}>{v.valor} <span style={st.n}>{v.productos}</span></span>
                ))}
                {f.cantidad > f.valores.length && <span style={st.mas}>y {f.cantidad - f.valores.length} más</span>}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

const st = {
  title: { margin: `0 0 ${space.sm}px`, fontSize: text.title, fontWeight: 600, color: colors.text },
  sub: { margin: `0 0 ${space.lg}px`, fontSize: text.sec, color: colors.textSec, maxWidth: 760, lineHeight: 1.5 },
  cats: { display: 'flex', flexWrap: 'wrap', gap: space.xs, marginBottom: space.md },
  cat: { padding: '5px 12px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, background: colors.bg, fontSize: text.sec, color: colors.text, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 },
  catActiva: { background: colors.linkBg, color: colors.link, borderColor: colors.link, fontWeight: 600 },
  catOcultos: { fontSize: text.meta, color: colors.textMeta, fontWeight: 400 },
  msg: { fontSize: text.sec, color: colors.textSec, margin: `0 0 ${space.md}px` },
  vacio: { color: colors.textMeta, fontSize: text.sec },
  tabla: { border: `1px solid ${colors.border}`, borderRadius: radius.md, background: colors.bg, overflow: 'hidden' },
  fila: { display: 'grid', gridTemplateColumns: '130px 200px 1fr', gap: space.md, padding: '10px 14px', borderBottom: `1px solid ${colors.borderSoft}`, alignItems: 'start' },
  filaOculta: { background: colors.bgSoft },
  cabecera: { background: colors.bgSoft, fontSize: text.meta, fontWeight: 600, color: colors.textSec, textTransform: 'uppercase', letterSpacing: '0.05em' },
  check: { display: 'flex', alignItems: 'center', gap: 8, fontSize: text.body, cursor: 'pointer' },
  nombre: { fontSize: text.body, fontWeight: 600, color: colors.text, display: 'flex', flexDirection: 'column', gap: 2 },
  cantidad: { fontSize: text.meta, fontWeight: 400, color: colors.textMeta },
  aviso: { fontSize: text.meta, fontWeight: 400, color: colors.warn },
  valores: { display: 'flex', flexWrap: 'wrap', gap: 4 },
  valor: { fontSize: text.meta, color: colors.text, background: colors.bgSoft, border: `1px solid ${colors.borderSoft}`, borderRadius: radius.xs, padding: '1px 6px' },
  n: { color: colors.textMeta },
  mas: { fontSize: text.meta, color: colors.textMeta, alignSelf: 'center' },
}
